import React, { useCallback, useMemo, useState } from 'react';
import VBox from '../../../components/layout/VBox';
import styles from './VisualizadorRelatorio.module.css';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import { dateWithoutTimezone } from '../../../lib/util';
import Botao from '../../../components/Botao';
import CampoMultiSeletor from '../../../components/Formulario/CampoMultiSeletor';
import {
  ReportBuildMetadata,
  StyleConfig,
  ColorRule,
} from '@tera/shared/src/types/ReportBuildMetadata';
import {
  mdiArrowRight,
  mdiChevronDown,
  mdiChevronUp,
  mdiDownload,
  mdiFilter,
  mdiFilterCog,
  mdiFilterCogOutline,
  mdiFilterPlusOutline,
  mdiOpenInNew,
  mdiPrinter,
} from '@mdi/js';
import Icon from '@/components/Icon';
import { translateFilter, translateFilterGenerico, PERIODO_RELATIVO_OPTIONS, resolvePeriodoRelativo, ReportFiltroAtivo, PERIODO_DIMENSION_FIELDS, periodoRangeFromDimensionValue } from '../util';
import CampoSeletor from '../../../components/Formulario/CampoSeletor';
import CampoData from '../../../components/Formulario/CampoData';
import HBox from '@/components/layout/HBox';
import Grid, { GridItem } from '@/components/layout/Grid';
import AlertBox from '@/components/layout/AlertBox';
import FiltroConstrutor from '@/components/Filtros/FiltroConstrutor';
import { useNavigate } from 'react-router-dom';

type Props = {
  titulo?: string;
  metricasDisponiveis?: {
    [key: string]: {
      nome: string;
      descricao: string;
      tipo: string;
      opcoes: string[];
    };
  };
  dimensoesDisponiveis?: {
    [key: string]: {
      nome: string;
      descricao: string;
      tipo: string;
      opcoes: string[];
    };
  };
  filtrosDisponiveis?: {
    [key: string]: {
      nome: string;
      descricao: string;
      tipo: string;
      opcoes: string[];
    };
  };
  metricas?: string[];
  dimensoes?: string[];
  filtro?: any;
  /** Filtro já serializado em JSON (formato FilterWhere). Quando informado,
   *  sobrescreve `filtro` e a função translateFilter não é chamada. */
  filtroRaw?: string;

  href?: string;
  nota?: React.ReactNode;
  csv?: boolean;
  /** Oculta o botão "Alterar dados" (útil quando o controle de filtros é externo) */
  hideAlterarDados?: boolean;
  /** Período padrão inicial. Quando fornecido, exibe widget de período e gerencia o filtro de data internamente */
  periodo?: string;
  periodoInicio?: string;
  periodoFim?: string;
  /** Filtros base como JSON array de ReportFiltroAtivo (sem filtro de data). Usado com `periodo` */
  filtrosBase?: string;
};

const ValorString = ({ valor }: { valor: string }) => {
  return <span>{valor}</span>;
};

const ValorNumero = ({ valor }: { valor: string }) => {
  return <span>{valor}</span>;
};

const ValorData = ({ valor }: { valor: string }) => {
  return <span>{valor}</span>;
};

const ValorBooleano = ({ valor }: { valor: string }) => {
  return <span>{valor === 'true' ? 'Verdadeiro' : 'Falso'}</span>;
};

/**
 * Aplica as regras de cores condicionais ao valor
 */
const applyColorRules = (value: any, colorRules?: ColorRule[]) => {
  if (!colorRules || colorRules.length === 0) return {};

  for (const rule of colorRules) {
    const { value: ruleValue, color, textColor, operator = 'equals' } = rule;
    let matches = false;

    switch (operator) {
      case 'equals':
        matches = value == ruleValue;
        break;
      case 'greater':
        matches = Number(value) > Number(ruleValue);
        break;
      case 'less':
        matches = Number(value) < Number(ruleValue);
        break;
      case 'greaterOrEqual':
        matches = Number(value) >= Number(ruleValue);
        break;
      case 'lessOrEqual':
        matches = Number(value) <= Number(ruleValue);
        break;
      case 'contains':
        matches = String(value).toLowerCase().includes(String(ruleValue).toLowerCase());
        break;
      case 'in':
        matches = Array.isArray(ruleValue) && ruleValue.includes(value);
        break;
    }

    if (matches) {
      return {
        backgroundColor: color,
        color: textColor || '#fff',
        padding: '4px 8px',
        borderRadius: '4px',
        display: 'inline-block',
      };
    }
  }

  return {};
};

/**
 * Aplica os estilos de fonte configurados
 */
const applyFontStyles = (styleConfig?: StyleConfig) => {
  if (!styleConfig?.fontStyle) return {};

  const { weight, style, size, transform, decoration } = styleConfig.fontStyle;

  return {
    fontWeight: weight,
    fontStyle: style,
    fontSize: size,
    textTransform: transform,
    textDecoration: decoration,
  };
};

/**
 * Componente que renderiza um valor com estilização baseada em metadados
 */
const StyledValue = ({
  valor,
  styleConfig,
  rowData,
}: {
  valor: any;
  styleConfig?: StyleConfig;
  rowData?: any;
}) => {
  const navigate = useNavigate();

  const colorStyles = useMemo(
    () => applyColorRules(valor, styleConfig?.colorRules),
    [valor, styleConfig?.colorRules],
  );

  const fontStyles = useMemo(
    () => applyFontStyles(styleConfig),
    [styleConfig],
  );

  const combinedStyles = { ...fontStyles, ...colorStyles };

  // Processa o link substituindo placeholders
  const processLink = (href: string) => {
    if (!rowData) return href;

    let processedHref = href;
    // Substitui {value} pelo valor atual
    processedHref = processedHref.replace('{value}', String(valor));

    // Substitui outros placeholders com dados da linha
    Object.keys(rowData).forEach(key => {
      processedHref = processedHref.replace(`{${key}}`, String(rowData[key]));
    });

    return processedHref;
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    if (!styleConfig?.link) return;

    const { href, type = 'internal', openInNewTab } = styleConfig.link;
    const processedHref = processLink(href);

    e.preventDefault();
    if (type === 'external' || openInNewTab) {
      window.open(processedHref, '_blank');
    } else {
      navigate(processedHref);
    }
  };

  const content = (
    <>
      {styleConfig?.icon && (
        <Icon path={styleConfig.icon} size={0.7} style={{ marginRight: '4px' }} />
      )}
      {styleConfig?.prefix && <span>{styleConfig.prefix}</span>}
      <span>{valor}</span>
      {styleConfig?.suffix && <span>{styleConfig.suffix}</span>}
    </>
  );

  if (styleConfig?.link) {
    const processedHref = processLink(styleConfig.link.href);
    return (
      <a
        href={processedHref}
        onClick={handleLinkClick}
        style={{
          ...combinedStyles,
          cursor: 'pointer',
          textDecoration: combinedStyles.textDecoration || 'underline',
          color: 'var(--tc-color-primary)',
        }}
        title={styleConfig.link.tooltip}
        className={styleConfig?.className}
      >
        {content}
        {styleConfig.link.type === 'external' && (
          <span style={{ marginLeft: '4px' }}>
            <Icon path={mdiOpenInNew} size={1} />
          </span>
        )}
      </a>
    );
  }

  return (
    <span style={combinedStyles} className={styleConfig?.className}>
      {content}
    </span>
  );
};

const formatarValor = (valor: string, tipo: string, formato?: string): string => {
  if (valor == null) return '--';

  if (formato === 'currency') {
    const num = Number(valor);
    return isNaN(num) ? valor : num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
  if (formato === 'percent') {
    const num = Number(valor);
    return isNaN(num) ? valor : num.toLocaleString('pt-BR', {
      style: 'percent',
      minimumFractionDigits: num < 0.1 ? 2 : 1,
      maximumFractionDigits: num < 0.1 ? 2 : 1,
    });
  }
  if (formato === 'number') {
    const num = Number(valor);
    return isNaN(num) ? valor : num.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }
  if (formato === 'duration') {
    const totalSec = Math.round(Number(valor));
    if (isNaN(totalSec)) return '--';
    const d = Math.floor(totalSec / 86400);
    const h = Math.floor((totalSec % 86400) / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const parts: string[] = [];
    if (d > 0) parts.push(`${d}d`);
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    if (s > 0 || parts.length === 0) parts.push(`${s}s`);
    return parts.join(' ');
  }

  if (tipo === 'date') return new Date(valor).toLocaleDateString();
  if (tipo === 'boolean') return valor === 'true' ? 'Verdadeiro' : 'Falso';

  return valor;
};

const ValorRelatorio = ({
  tipo,
  valor,
  formato,
  styleConfig,
  rowData,
}: {
  tipo: string;
  valor: string;
  formato?: string;
  styleConfig?: StyleConfig;
  rowData?: any;
}) => {
  const displayValue = formatarValor(valor, tipo, formato);

  if (styleConfig) {
    return <StyledValue valor={displayValue} styleConfig={styleConfig} rowData={rowData} />;
  }

  return <span>{displayValue}</span>;
};

const VisualizadorRelatorio = ({
  titulo,
  filtro,
  filtroRaw,
  href,
  metricas,
  dimensoes,

  metricasDisponiveis,
  dimensoesDisponiveis,

  nota,
  csv,
  hideAlterarDados,
  periodo,
  periodoInicio,
  periodoFim,
  filtrosBase,
}: Props) => {
  const [showFilter, setShowFilter] = useState(false);

  // Estado draft (editável pelo usuário, não envia query ainda)
  const [draftMetricas, setDraftMetricas] = useState<string[]>(metricas ?? []);
  const [draftDimensoes, setDraftDimensoes] = useState<string[]>(dimensoes ?? []);

  // Estado committed (o que está efetivamente na query e na tabela)
  const [committedMetricas, setCommittedMetricas] = useState<string[]>(metricas ?? []);
  const [committedDimensoes, setCommittedDimensoes] = useState<string[]>(dimensoes ?? []);

  // Estado do período — draft
  const [draftPeriodo, setDraftPeriodo] = useState(periodo ?? 'mes');
  const [draftPeriodoInicio, setDraftPeriodoInicio] = useState(periodoInicio ?? '');
  const [draftPeriodoFim, setDraftPeriodoFim] = useState(periodoFim ?? '');
  const { dataInicio: previewInicio, dataFim: previewFim } = useMemo(
    () => resolvePeriodoRelativo(draftPeriodo, draftPeriodoInicio, draftPeriodoFim),
    [draftPeriodo, draftPeriodoInicio, draftPeriodoFim]
  );

  // Estado do período — committed
  const [committedPeriodo, setCommittedPeriodo] = useState(periodo ?? 'mes');
  const [committedPeriodoInicio, setCommittedPeriodoInicio] = useState(periodoInicio ?? '');
  const [committedPeriodoFim, setCommittedPeriodoFim] = useState(periodoFim ?? '');

  // Estado dos filtros genéricos (Filtros configurados em "Alterar dados") — draft x committed
  const filtrosIniciais = useMemo<ReportFiltroAtivo[]>(() => {
    try { return JSON.parse(filtrosBase || '[]'); } catch { return []; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [draftFiltros, setDraftFiltros] = useState<ReportFiltroAtivo[]>(filtrosIniciais);
  const [committedFiltros, setCommittedFiltros] = useState<ReportFiltroAtivo[]>(filtrosIniciais);

  // Só é montado quando o metadata chega (ver efeito abaixo): sem metadata.filters o valueMap
  // não é aplicado e filtros como "ruim" iriam crus para o backend (comparados com avg numérico).
  const [committedFiltroRaw, setCommittedFiltroRaw] = useState<string | undefined>(undefined);
  const [execRefreshKey, setExecRefreshKey] = useState(0);
  const activeFiltroRaw = periodo !== undefined ? committedFiltroRaw : filtroRaw;

  // Dirty: parâmetros foram alterados mas ainda não aplicados
  const isDirty = useMemo(() => {
    if (draftMetricas.join(',') !== committedMetricas.join(',')) return true;
    if (draftDimensoes.join(',') !== committedDimensoes.join(',')) return true;
    if (periodo !== undefined && (
      draftPeriodo !== committedPeriodo ||
      draftPeriodoInicio !== committedPeriodoInicio ||
      draftPeriodoFim !== committedPeriodoFim ||
      JSON.stringify(draftFiltros) !== JSON.stringify(committedFiltros)
    )) return true;
    return false;
  }, [draftMetricas, committedMetricas, draftDimensoes, committedDimensoes,
    draftPeriodo, committedPeriodo, draftPeriodoInicio, committedPeriodoInicio,
    draftPeriodoFim, committedPeriodoFim, draftFiltros, committedFiltros, periodo]);

  const isMetrDimDirty = useMemo(() => {
    if (metricas?.join(',') !== committedMetricas.join(',')) return true;
    if (dimensoes?.join(',') !== committedDimensoes.join(',')) return true;
    if (metricas?.join(',') !== draftMetricas.join(',')) return true;
    if (dimensoes?.join(',') !== draftDimensoes.join(',')) return true;
    if (periodo !== undefined) {
      if (JSON.stringify(filtrosIniciais) !== JSON.stringify(committedFiltros)) return true;
      if (JSON.stringify(filtrosIniciais) !== JSON.stringify(draftFiltros)) return true;
    }
    return false;
  }, [metricas, committedMetricas, dimensoes, committedDimensoes, draftMetricas, draftDimensoes,
    periodo, filtrosIniciais, committedFiltros, draftFiltros]);


  // Função para imprimir o relatório
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // Função para formatar filtros aplicados para exibição
  const formatarFiltrosAplicados = useCallback(() => {
    if (!filtro) return 'Nenhum filtro aplicado';

    const filtrosTexto = [];

    if (filtro.periodo) {
      const [tipo, inicio, fim] = filtro.periodo;
      filtrosTexto.push(`Período: ${inicio} até ${fim} (${tipo})`);
    }

    if (filtro.canal_id && filtro.canal_id.length > 0) {
      filtrosTexto.push(`Canais: ${filtro.canal_id.length} selecionado(s)`);
    }

    if (filtro.grupo_id && filtro.grupo_id.length > 0) {
      filtrosTexto.push(`Grupos: ${filtro.grupo_id.length} selecionado(s)`);
    }

    return filtrosTexto.join(' • ');
  }, [filtro]);


  const downloadCsvMutator = useMutation(async () => {
    const qs = new URLSearchParams();
    qs.append('facts', committedMetricas.join(','));
    qs.append('dimensions', committedDimensoes.join(','));
    qs.append('filter', activeFiltroRaw !== undefined ? activeFiltroRaw : translateFilter(filtro));
    qs.append('format', 'csv');

    const res = await axios.get(href + '?' + qs.toString(), {
      responseType: 'blob',
    });

    // Cria um link para download do arquivo CSV
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;


    const reportFileName = titulo?.replace(/\s+/g, '_').toLowerCase() || 'relatorio';
    link.setAttribute(
      'download',
      `${reportFileName}_${dateWithoutTimezone(new Date()).replace(/:/g, '-')}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  });

  const qMetadata = useQuery(
    ['metadata', href],
    async () => {
      const res = await axios.get(href + '/metadata');
      return res.data.result;
    },
    {
      refetchOnWindowFocus: false,
    },
  );
  const metadata: ReportBuildMetadata = qMetadata.data;

  // Metadados de filtros sem os campos do tipo date (período é controlado separadamente)
  const filtersMetadataSemData = useMemo(() => {
    if (!metadata?.filters) return {};
    return Object.fromEntries(
      Object.entries(metadata.filters).filter(([, f]) => (f as any).type !== 'date')
    );
  }, [metadata?.filters]);
  const hasNonDateFilters = Object.keys(filtersMetadataSemData).length > 0;

  // Resolve para qual filtro oficial uma dimensão clicada deve ir. Quando a dimensão em si já
  // é um filtro declarado (ex: "canal_tipo"), usa-a diretamente. Quando é uma dimensão de nome
  // (ex: "usuario_responsavel_nome") cujo filtro correspondente é por id/lookup (ex:
  // "usuario_responsavel_id"), aponta para o filtro por id — o valor será resolvido via lookup
  // no momento do clique, para que "Responsável atendimento" e "Responsável" sejam o mesmo filtro.
  const getFiltroAlvo = useCallback(
    (dimField: string): { filterField: string; viaLookup: boolean } | null => {
      if (metadata?.filters?.[dimField] && metadata.filters[dimField].type !== 'date') {
        return { filterField: dimField, viaLookup: false };
      }
      if (dimField.endsWith('_nome')) {
        const idField = dimField.slice(0, -'_nome'.length) + '_id';
        if (metadata?.filters?.[idField]?.lookup) {
          return { filterField: idField, viaLookup: true };
        }
      }
      return null;
    },
    [metadata?.filters],
  );

  // Reconstrói committedFiltroRaw com metadata.filters (valueMap) assim que o metadata chegar
  const prevMetadataRef = React.useRef<ReportBuildMetadata | undefined>(undefined);
  React.useEffect(() => {
    if (periodo === undefined) return;
    if (!metadata?.filters) return;
    if (prevMetadataRef.current === metadata) return; // já processou este metadata
    prevMetadataRef.current = metadata;
    const { dataInicio, dataFim } = resolvePeriodoRelativo(committedPeriodo, committedPeriodoInicio, committedPeriodoFim);
    setCommittedFiltroRaw(translateFilterGenerico(
      [...committedFiltros, { field: 'data_criacao', type: 'date' as const, operator: 'between' as const, value: [dataInicio, dataFim] }],
      metadata.filters
    ));
    setExecRefreshKey(k => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata]);

  const q = useQuery(
    ['visualizador', committedMetricas, committedDimensoes, filtro, activeFiltroRaw, href, execRefreshKey],
    async () => {
      const qs = new URLSearchParams();
      qs.append('facts', committedMetricas.join(','));
      qs.append('dimensions', committedDimensoes.join(','));
      qs.append('filter', activeFiltroRaw !== undefined ? activeFiltroRaw : translateFilter(filtro));

      const res = await axios.get(href + '?' + qs.toString());

      return res.data.result;
    },
    {
      enabled: periodo === undefined || committedFiltroRaw !== undefined,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );

  const metricasOptions = metadata?.metrics
    ? Object.values(metadata.metrics).map(m => ({
      id: m.name,
      label: m.label,
      format: m.format,
    }))
    : [];

  const dimensoesOptions = metadata?.dimensions
    ? Object.values(metadata.dimensions).map(m => ({
      id: m.name,
      label: m.label,
      format: m.format,
    }))
    : [];

  const metricDownTo = useCallback(
    (metrica: string) => {
      if (!metadata?.metrics?.[metrica]?.downTo) return;

      const type = metadata.metrics[metrica].downTo!.type;
      const name = metadata.metrics[metrica].downTo!.name;

      const novasMetricas = committedMetricas.filter(m => m !== metrica);

      if (type === 'dimension') {
        const novasDimensoes = [name, ...committedDimensoes];
        setDraftDimensoes(novasDimensoes);
        setCommittedDimensoes(novasDimensoes);
      } else if (type === 'metric') {
        novasMetricas.unshift(name);
      }
      setDraftMetricas(novasMetricas);
      setCommittedMetricas(novasMetricas);
    },
    [committedMetricas, committedDimensoes, metadata?.metrics],
  );

  // Drill-down: clicar em um valor de dimensão substitui o filtro para aquele campo pelo valor clicado.
  // Quando a dimensão é resolvida por lookup (ex: nome → id), o valor clicado é traduzido para o id
  // e SUBSTITUI o valor do MESMO filtro oficial (ex: "Responsável"), em vez de criar um filtro paralelo
  // ou acumular seleções — mesmo que o filtro já esteja com operador "in" e outros valores selecionados.
  const applyFilterValue = useCallback(
    async (dimField: string, value: any) => {
      if (periodo === undefined || !metadata?.filters) return;
      const alvo = getFiltroAlvo(dimField);
      if (!alvo) return;

      let novosFiltros: ReportFiltroAtivo[];

      if (alvo.viaLookup) {
        const filterMeta = metadata.filters[alvo.filterField];
        try {
          const res = await axios.get(filterMeta.lookup!.href);
          const items: Record<string, any>[] = res.data.result ?? res.data ?? [];
          const idKey = filterMeta.lookup!.idKey ?? 'id';
          const labelKey = filterMeta.lookup!.labelKey ?? 'nome';
          const match = items.find(it => String(it[labelKey]) === String(value));
          if (!match) return;
          const id = match[idKey];
          novosFiltros = [
            ...committedFiltros.filter(f => f.field !== alvo.filterField),
            { field: alvo.filterField, type: filterMeta.type, operator: 'in', value: [id] },
          ];
        } catch {
          return;
        }
      } else {
        novosFiltros = [
          ...committedFiltros.filter(f => f.field !== alvo.filterField),
          { field: alvo.filterField, type: metadata.dimensions[dimField].type, operator: 'eq', value },
        ];
      }

      setDraftFiltros(novosFiltros);
      setCommittedFiltros(novosFiltros);
      const { dataInicio, dataFim } = resolvePeriodoRelativo(committedPeriodo, committedPeriodoInicio, committedPeriodoFim);
      setCommittedFiltroRaw(translateFilterGenerico(
        [...novosFiltros, { field: 'data_criacao', type: 'date' as const, operator: 'between' as const, value: [dataInicio, dataFim] }],
        metadata.filters
      ));
      setExecRefreshKey(k => k + 1);
    },
    [periodo, committedFiltros, committedPeriodo, committedPeriodoInicio, committedPeriodoFim, metadata, getFiltroAlvo],
  );

  // Drill-down de período: clicar em um valor de "Período (dia/semana/mês)" troca o período
  // selecionado para o intervalo exato daquele dia/semana/mês, substituindo o período atual.
  const applyPeriodoDrillDown = useCallback(
    (dimField: string, rawValue: any) => {
      if (periodo === undefined) return;
      const range = periodoRangeFromDimensionValue(dimField, rawValue);
      if (!range) return;

      setDraftPeriodo('custom');
      setDraftPeriodoInicio(range.dataInicio);
      setDraftPeriodoFim(range.dataFim);
      setCommittedPeriodo('custom');
      setCommittedPeriodoInicio(range.dataInicio);
      setCommittedPeriodoFim(range.dataFim);
      setCommittedFiltroRaw(translateFilterGenerico(
        [...committedFiltros, { field: 'data_criacao', type: 'date' as const, operator: 'between' as const, value: [range.dataInicio, range.dataFim] }],
        metadata?.filters
      ));
      setExecRefreshKey(k => k + 1);
    },
    [periodo, committedFiltros, metadata?.filters],
  );

  return (
    <VBox>
      {nota && <AlertBox variant="info">{nota}</AlertBox>}
      {periodo !== undefined && (
        <div className="no-print" style={{ marginBottom: 8 }}>
          <Grid>
            <GridItem sm={12} md={4}>
              <CampoSeletor
                label="Período"
                noSearch
                options={PERIODO_RELATIVO_OPTIONS.map(o => ({ id: o.id, label: o.label }))}
                value={draftPeriodo}
                onChange={v => setDraftPeriodo(v ?? 'mes')}
              />
            </GridItem>
            <GridItem sm={12} md={4}>
              <CampoData
                label="De"
                value={draftPeriodo === 'custom' ? draftPeriodoInicio : previewInicio}
                onChange={draftPeriodo === 'custom' ? setDraftPeriodoInicio : undefined}
                disabled={draftPeriodo !== 'custom'}
              />
            </GridItem>
            <GridItem sm={12} md={4}>
              <CampoData
                label="Até"
                value={draftPeriodo === 'custom' ? draftPeriodoFim : previewFim}
                onChange={draftPeriodo === 'custom' ? setDraftPeriodoFim : undefined}
                disabled={draftPeriodo !== 'custom'}
              />
            </GridItem>
          </Grid>
        </div>
      )}
      {showFilter && (
        <div className="no-print">
          <Grid>
            <GridItem sm={12}>
              <CampoMultiSeletor
                label="Métricas"
                options={metricasOptions}
                value={draftMetricas}
                onChange={setDraftMetricas}
              />
            </GridItem>
            <GridItem sm={12}>
              <CampoMultiSeletor
                label="Dimensões"
                options={dimensoesOptions}
                value={draftDimensoes}
                onChange={setDraftDimensoes}
              />
            </GridItem>
            {periodo !== undefined && hasNonDateFilters && (
              <GridItem sm={12}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: '0.875rem' }}>Filtros</label>
                <FiltroConstrutor
                  filtersMetadata={filtersMetadataSemData as any}
                  reportHref={href ?? ''}
                  value={draftFiltros}
                  onChange={setDraftFiltros}
                />
              </GridItem>
            )}
          </Grid>
        </div>
      )}

      <HBox style={{ justifyContent: 'flex-end' }} className="no-print">
        {csv && (
          <Botao
            variant="outline-secondary"
            onClick={() => downloadCsvMutator.mutate()}
            icon={mdiDownload}
          >
            CSV
          </Botao>
        )}
        <Botao
          variant="outline-secondary"
          onClick={handlePrint}
          icon={mdiPrinter}
          className="print-button"
          title="Imprimir relatório"
        >
          Imprimir
        </Botao>
        {!hideAlterarDados && (
          <Botao
            variant="outline-secondary"
            onClick={() => setShowFilter(!showFilter)}
            icon={isMetrDimDirty ? mdiFilterCog : mdiFilterCogOutline}
          >
            Alterar dados{isMetrDimDirty ? ' *' : ''}
            <Icon path={showFilter ? mdiChevronUp : mdiChevronDown} size={1} />
          </Botao>
        )}
        <Botao
          variant={isDirty ? 'primary' : 'outline-primary'}
          onClick={() => {
            setCommittedMetricas(draftMetricas);
            setCommittedDimensoes(draftDimensoes);
            if (periodo !== undefined) {
              setCommittedPeriodo(draftPeriodo);
              setCommittedPeriodoInicio(draftPeriodoInicio);
              setCommittedPeriodoFim(draftPeriodoFim);
              setCommittedFiltros(draftFiltros);
              const { dataInicio, dataFim } = resolvePeriodoRelativo(draftPeriodo, draftPeriodoInicio, draftPeriodoFim);
              setCommittedFiltroRaw(translateFilterGenerico(
                [...draftFiltros, { field: 'data_criacao', type: 'date' as const, operator: 'between' as const, value: [dataInicio, dataFim] }],
                metadata?.filters
              ));
              setExecRefreshKey(k => k + 1);
            } else {
              q.refetch();
            }
            setShowFilter(false);
          }}
        >
          Atualizar{isDirty ? ' *' : ''}
        </Botao>
      </HBox>

      {/* Informações do relatório para impressão */}
      <div className="relatorio-info print-only" style={{ marginBottom: '1rem' }}>
        <HBox style={{ alignItems: 'center', marginBottom: '0.5rem', gap: '1rem' }}>
          <img
            src="/logo.png"
            alt="Terachat Logo"
            style={{ height: '40px', width: 'auto' }}
          />
          <h3 style={{ margin: '0', color: '#333', flex: 1 }}>
            Relatório de Dados
          </h3>
        </HBox>
        <div className="filtros-aplicados" style={{ fontSize: '0.9rem', color: '#666' }}>
          <strong>Filtros aplicados:</strong> {formatarFiltrosAplicados()}
        </div>
        <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
          <strong>Gerado em:</strong> {new Date().toLocaleString('pt-BR')}
        </div>
        <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
          <strong>Total de registros:</strong> {q.data?.length || 0}
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>#</th>
            {committedMetricas.map(m => (
              <th key={m}>{metadata?.metrics?.[m]?.label ?? m}</th>
            ))}
            {committedDimensoes.map(d => (
              <th key={d}>{metadata?.dimensions?.[d]?.label ?? d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {q.isFetching ? (
            <tr>
              <td
                colSpan={
                  committedMetricas.length + committedDimensoes.length + 2
                }
              >
                Carregando...
              </td>
            </tr>
          ) : (
            q.data?.map((d: any, idx: number) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                {committedMetricas.map(m => (
                  <td key={`${m}-${idx}`}>
                    {metadata?.metrics?.[m]?.downTo ? (
                      <Botao
                        variant="none-primary"
                        onClick={() => metricDownTo(m)}
                      >
                        <ValorRelatorio
                          tipo={metadata.metrics[m].type}
                          valor={d[m]}
                          formato={metadata.metrics[m].format}
                          styleConfig={metadata.metrics[m].style}
                          rowData={d}
                        />
                        <Icon path={mdiArrowRight} size={0.8} />
                      </Botao>
                    ) : (
                      <ValorRelatorio
                        tipo={metadata?.metrics?.[m]?.type}
                        valor={d[m]}
                        formato={metadata.metrics[m].format}
                        styleConfig={metadata?.metrics?.[m]?.style}
                        rowData={d}
                      />
                    )}
                  </td>
                ))}
                {committedDimensoes.map(dd => {
                  const isPeriodoDim = (PERIODO_DIMENSION_FIELDS as readonly string[]).includes(dd);
                  const podeFiltrar = periodo !== undefined
                    && (isPeriodoDim || !!getFiltroAlvo(dd))
                    && !metadata?.dimensions?.[dd]?.style?.link
                    && d[dd] != null;
                  return (
                    <td key={`${dd}-${idx}`}>
                      {podeFiltrar ? (
                        <Botao
                          variant="none-primary"
                          onClick={() => isPeriodoDim ? applyPeriodoDrillDown(dd, d[dd]) : applyFilterValue(dd, d[dd])}
                          title={isPeriodoDim ? 'Filtrar por este período' : `Filtrar por "${d[dd]}"`}
                        >
                          <ValorRelatorio
                            tipo={metadata?.dimensions?.[dd]?.type}
                            valor={d[dd]}
                            formato={metadata.dimensions[dd].format}
                            styleConfig={metadata?.dimensions?.[dd]?.style}
                            rowData={d}
                          />
                          <Icon path={mdiFilterPlusOutline} size={0.8} />
                        </Botao>
                      ) : (
                        <ValorRelatorio
                          tipo={metadata?.dimensions?.[dd]?.type}
                          valor={d[dd]}
                          formato={metadata.dimensions[dd].format}
                          styleConfig={metadata?.dimensions?.[dd]?.style}
                          rowData={d}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </VBox>
  );
};

export default VisualizadorRelatorio;
